import React, { useEffect, useState } from 'react';
import { api } from '../../api';
import { fmtDate } from '../../dateUtil';
import { formatCurrency } from '../../currencyUtil';

function money(n) {
  if (n == null) return '—';
  return `$${formatCurrency(n)}`;
}

function StatCard({ label, value, sub, color = 'slate' }) {
  const colors = {
    blue:  'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',
    green: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300',
    amber: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300',
    slate: 'bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300',
  };
  return (
    <div className={`min-w-0 rounded-lg border border-slate-200 dark:border-slate-700 p-4 sm:p-5 ${colors[color]}`}>
      <p className="text-xs font-medium uppercase tracking-wider opacity-70 mb-1">{label}</p>
      <p className="text-xl sm:text-3xl font-bold break-words">{value ?? '—'}</p>
      {sub && <p className="text-xs mt-1 opacity-60">{sub}</p>}
    </div>
  );
}

function daysAgoISO(n) {
  return new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
}
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function Reports() {
  const [from, setFrom]       = useState(daysAgoISO(90));
  const [to, setTo]           = useState(todayISO());
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get(`/reports/summary?from=${from}&to=${to}`)
      .then(d  => { if (mounted) setSummary(d); })
      .catch(e => { if (mounted) setError(e.message); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [from, to]);

  return (
    <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-lg font-semibold text-slate-900 dark:text-white">Reports</h1>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <label className="text-slate-500 dark:text-slate-400">From</label>
          <input type="date" className="input" value={from} max={to} onChange={e => setFrom(e.target.value)} />
          <label className="text-slate-500 dark:text-slate-400">To</label>
          <input type="date" className="input" value={to} min={from} max={todayISO()} onChange={e => setTo(e.target.value)} />
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">{error}</div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : summary && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <StatCard label="Cars Sold" value={summary.count_sold} color="slate" />
            <StatCard label="Total Revenue" value={money(summary.total_revenue)} color="blue" />
            <StatCard label="Avg Sale Price" value={money(summary.avg_sale_price)} color="blue" />
            <StatCard
              label="Total Margin"
              value={summary.total_margin == null ? '—' : money(summary.total_margin)}
              sub={summary.cars_without_cost_basis > 0
                ? `${summary.cars_without_cost_basis} sold car${summary.cars_without_cost_basis === 1 ? '' : 's'} missing a cost basis`
                : null}
              color="green"
            />
            <StatCard
              label="Avg Margin / Car"
              value={summary.avg_margin == null ? '—' : money(summary.avg_margin)}
              color="green"
            />
            <StatCard
              label="Avg Days to Sale"
              value={summary.avg_days_to_sale == null ? '—' : summary.avg_days_to_sale.toFixed(1)}
              color="amber"
            />
          </div>

          <div>
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Sales by Buyer</h2>
            {!summary.by_buyer.length ? (
              <p className="text-sm text-slate-400">No sales in this range.</p>
            ) : (
              <table className="w-full text-sm border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Buyer Lot</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Cars</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Revenue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {summary.by_buyer.map(b => (
                    <tr key={b.location_id ?? 'unknown'}>
                      <td className="px-4 py-2.5 text-slate-800 dark:text-slate-100">{b.name}</td>
                      <td className="px-4 py-2.5 text-right text-slate-600 dark:text-slate-400">{b.count}</td>
                      <td className="px-4 py-2.5 text-right font-medium text-slate-800 dark:text-slate-100">{money(b.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div>
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Oldest Unsold Inventory</h2>
            {!summary.aging_inventory.length ? (
              <p className="text-sm text-slate-400">No unsold cars.</p>
            ) : (
              <table className="w-full text-sm border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Vehicle</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider hidden md:table-cell">VIN</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider hidden md:table-cell">Listed</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Days Listed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {summary.aging_inventory.map(c => (
                    <tr key={c.id}>
                      <td className="px-4 py-2.5 text-slate-800 dark:text-slate-100">
                        {[c.year, c.make, c.model, c.trim].filter(Boolean).join(' ') || `Car #${c.id}`}
                      </td>
                      <td className="px-4 py-2.5 hidden md:table-cell text-slate-500 dark:text-slate-400">{c.vin || '—'}</td>
                      <td className="px-4 py-2.5 hidden md:table-cell text-slate-500 dark:text-slate-400">{fmtDate(c.created_at)}</td>
                      <td className="px-4 py-2.5 text-right font-medium text-slate-800 dark:text-slate-100">{c.days_in_inventory}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
